/* eslint-disable complexity */
import {
  faArrowToBottom,
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CommonButton from 'app/components/ReviewButton';
import { downloadUrl } from 'app/utils/downloadUrl';
import { t } from 'i18next';
import { useEffect, useRef } from 'react';

import { DownloadModelAuditReportButton } from './DownloadModelAuditReportButton';
import { ModelAuditReportDescription } from './ModelAuditReportDescription';
import { MODEL_AUDIT_REPORT_TASK_NAME } from './modelAuditReportTask';
import { ViewClusteringButton } from './ViewClusteringButton';
import { ViewErrorButton } from './ViewErrorButton';
import { ViewIntelligentTopUpImagesButton } from './ViewIntelligentTopUpImagesButton';
import { ViewSegregateImagesButton } from './ViewSegregateImagesButton';
import { ViewSimilarImagesButton } from './ViewSimilarImagesButton';
import { ViewUniqueImagesButton } from './ViewUniqueImagesButton';

const useStyles = makeStyles(theme => ({
  header: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.colors.grey[16],
    wordWrap: 'break-word',
  },
  timestamp: {
    marginTop: '4px',
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: theme.colors.grey[10],
  },
  circularProgress: {
    marginRight: theme.spacing(1),
    '& svg': {
      color: theme.colors.blue[600],
    },
  },
  failedIcon: {
    fontSize: '0.75rem',
    color: theme.colors.red[600],
    marginRight: theme.spacing(1),
  },
  successIcon: {
    fontSize: '0.75rem',
    color: theme.colors.green[600],
    marginRight: theme.spacing(1),
  },
  actionButtons: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
}));

const parseKwargs = str => {
  if (
    !str ||
    typeof str !== 'string' ||
    !str.startsWith('{') ||
    !str.endsWith('}')
  ) {
    return null;
  }
  try {
    return JSON.parse(str);
  } catch {
    try {
      const formatted = str
        .replace(/([{\s,])'|'([\s,:}])/g, '$1"$2')
        .replace(/True/g, 'true')
        .replace(/False/g, 'false')
        .replace(/None/g, 'null');
      return JSON.parse(formatted);
    } catch {
      const out = Object.create(null);
      const pairs =
        str
          .slice(1, -1)
          .match(
            /(?:'[^']*'|"[^"]*"|[^\s,'"{}]+)\s*:\s*(?:'[^']*'|"[^"]*"|[^\s,'"{}]+)/g
          ) || [];
      for (const pair of pairs) {
        // The regex above only matches `key : value`, so a colon is always present.
        const colonIdx = pair.indexOf(':');
        const k = pair
          .slice(0, colonIdx)
          .trim()
          .replace(/^['"]|['"]$/g, '')
          .trim();
        const v = pair
          .slice(colonIdx + 1)
          .trim()
          .replace(/^['"]|['"]$/g, '')
          .trim();
        if (k && !['__proto__', 'constructor', 'prototype'].includes(k)) {
          out[k] = v;
        }
      }
      return out;
    }
  }
};

const TaskCard = ({
  title,
  status,
  timestamp,
  onRetryClick,
  taskName,
  url = null,
  setSize,
  index,
  windowHeight,
  taskId,
  task_kwargs,
  dataComposition,
  errors,
  ...rest
}) => {
  const rowRef = useRef();

  useEffect(() => {
    setSize(index, rowRef.current.getBoundingClientRect().height);
  }, [setSize, index, windowHeight]);

  const classes = useStyles();
  const renderIcons = () => {
    switch (status) {
      case 'STARTED':
      case 'PENDING':
      case 'RETRY':
        return (
          <CircularProgress size={12} className={classes.circularProgress} />
        );

      case 'FAILURE':
        return (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className={classes.failedIcon}
          />
        );

      case 'SUCCESS':
        return (
          <FontAwesomeIcon
            icon={faCheckCircle}
            className={classes.successIcon}
          />
        );

      default:
        return null;
    }
  };

  const downloadHandler = () => downloadUrl(url);

  const renderUniqueImagesButtons = kwargs => {
    const rawVal = kwargs?.is_trained_model_misclassified_images;
    const isIntelligentTopUp =
      rawVal === true || String(rawVal).toLowerCase() === 'true';

    return (
      <Box className={classes.actionButtons}>
        {isIntelligentTopUp ? (
          <ViewIntelligentTopUpImagesButton
            tagId={Number(kwargs?.file_set_tag_id || '0')}
            useCaseId={Number(kwargs?.use_case_id || '0')}
            mlModelId={Number(kwargs?.ml_model_id || '0')}
          />
        ) : (
          <ViewUniqueImagesButton
            tagId={Number(kwargs?.file_set_tag_id || '0')}
            useCaseId={Number(kwargs?.use_case_id || '0')}
          />
        )}
      </Box>
    );
  };

  const isModelAuditReport = taskName === MODEL_AUDIT_REPORT_TASK_NAME;

  // The audit report's description is card body text (date range + use
  // cases), so its title is fixed FE text keyed on the task name.
  const renderTitle = isModelAuditReport ? (
    <>
      <Typography className={classes.header}>
        {t('Model Audit Report')}
      </Typography>
      <ModelAuditReportDescription description={title} />
    </>
  ) : (
    <Typography className={classes.header}>{t(title)}</Typography>
  );

  const taskKwargs = parseKwargs(task_kwargs);

  return (
    <Box width='400' {...rest} display='flex' alignItems='flex-start'>
      <Box
        display='flex'
        borderBottom='1px solid #E8F2FE'
        alignItems='baseline'
        width='100%'
        margin='0px 16px 0px 16px'
        {...{ ref: rowRef }}
      >
        <Box>{renderIcons()}</Box>
        <Box width='290px' padding='16px 0px 16px 0px'>
          {renderTitle}
          {timestamp ? (
            <Typography className={classes.timestamp}>{timestamp}</Typography>
          ) : null}
          {status === 'FAILURE' && (
            <ViewErrorButton onClick={onRetryClick} errors={errors} />
          )}
          {taskName === 'async_generate_similar_images' &&
            status === 'SUCCESS' && (
              <Box className={classes.actionButtons}>
                <CommonButton
                  text='Download'
                  onClick={downloadHandler}
                  variant='tertiary'
                  icon={<FontAwesomeIcon icon={faArrowToBottom} />}
                  toolTip
                  toolTipMsg='Download to analyse the model prediction. Then go to “View all similar image” to update the labels.'
                />
                <ViewSimilarImagesButton taskId={taskId} />
              </Box>
            )}
          {taskName === 'async_generate_unique_images' &&
            status === 'SUCCESS' &&
            renderUniqueImagesButtons(taskKwargs)}
          {taskName === 'segregate_train_test_data' && status === 'SUCCESS' && (
            <Box className={classes.actionButtons}>
              <ViewSegregateImagesButton
                segregateKwargs={taskKwargs}
                dataComposition={dataComposition}
              />
            </Box>
          )}
          {taskName === 'async_cluster' &&
            status === 'SUCCESS' &&
            taskKwargs?.cluster_run_id && (
              <Box className={classes.actionButtons}>
                <ViewClusteringButton
                  clusterRunId={Number(taskKwargs.cluster_run_id)}
                />
              </Box>
            )}
          {(taskName === 'export_images_to_zip' ||
            taskName === 'export_wafermap_data_to_excel') &&
            status === 'SUCCESS' && (
              <Box className={classes.actionButtons}>
                <CommonButton
                  text='Download'
                  onClick={downloadHandler}
                  variant='tertiary'
                  icon={<FontAwesomeIcon icon={faArrowToBottom} />}
                  toolTip
                  toolTipMsg={t('Download to analyse the wafermap images.')}
                />
              </Box>
            )}
          {isModelAuditReport && status === 'SUCCESS' && (
            <Box className={classes.actionButtons}>
              <DownloadModelAuditReportButton taskId={taskId} />
            </Box>
          )}
          {taskName === 'export_yield_report_to_zip' &&
            status === 'SUCCESS' && (
              <Box className={classes.actionButtons}>
                <CommonButton
                  text='Download'
                  onClick={downloadHandler}
                  variant='tertiary'
                  icon={<FontAwesomeIcon icon={faArrowToBottom} />}
                  toolTip
                  toolTipMsg='Download the Yield report'
                />
              </Box>
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default TaskCard;
